import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Tooltip } from 'antd';
import { CommentOutlined, CloseOutlined, SendOutlined, ClearOutlined } from '@ant-design/icons';
import { useUser } from '../../UserContext';
import { findHelpTarget } from '../HelpSpotlight';
import { askAssistant } from './assistantApi';
import AssistantAnswer from './AssistantAnswer';
import './Assistant.css';

const HELP_WINDOW = 'width=700,height=820,left=0,top=0';

// Asks the order page which orders are selected; it answers the help window the same way
function selectedOrders(channel) {
  return new Promise((resolve) => {
    let timer = null;
    function done(orders) {
      clearTimeout(timer);
      channel.removeEventListener('message', onMessage);
      resolve(orders);
    }
    function onMessage(e) {
      if (e.data?.type === 'context') done((e.data.orders || []).map((o) => ({ status: o.status })));
    }
    timer = setTimeout(() => done([]), 200);
    channel.addEventListener('message', onMessage);
    channel.postMessage({ type: 'request-context' });
  });
}

// Question and answer pairs, for follow-up questions
function history(items) {
  const pairs = [];
  items.forEach((item, i) => {
    if (item.kind === 'a' && items[i - 1]?.kind === 'q') pairs.push({ question: items[i - 1].text, answer: item.text });
  });
  return pairs.slice(-3);
}

const overlaps = (a, b) => a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;

/** The chat bubble in the corner: questions about using the ERP, answered from the help. */
const AssistantChat = () => {
  const { fetchWithAuth } = useUser();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]); // {kind: 'q' | 'a' | 'error', text, id?, covered?}
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState('');
  const channelRef = useRef(null);
  const listRef = useRef(null);
  const panelRef = useRef(null);
  const pendingRef = useRef(null); // the element we last asked the spotlight to show

  useEffect(() => {
    const channel = new BroadcastChannel('innocoso-help');
    channel.addEventListener('message', (e) => {
      if (e.data?.type === 'spotlight-miss' && e.data.helpId === pendingRef.current) {
        setOpen(true);
        setHint('That isn\'t on the screen right now. Open the page or dialog it belongs to, then click it again.');
      }
    });
    channelRef.current = channel;
    return () => channel.close();
  }, []);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [items, busy, open]);

  const send = async () => {
    const question = draft.trim();
    if (!question || busy) return;
    const earlier = history(items);
    setDraft('');
    setHint('');
    setBusy(true);
    setItems((prev) => [...prev, { kind: 'q', text: question }]);
    const selection = await selectedOrders(channelRef.current);
    const result = await askAssistant(fetchWithAuth, { question, page: window.location.pathname, selection, history: earlier });
    setItems((prev) => [...prev, result.ok
      ? { kind: 'a', id: result.data.id, text: result.data.answer, covered: result.data.covered }
      : { kind: 'error', text: result.error }]);
    setBusy(false);
  };

  const showElement = (helpId) => {
    setHint('');
    pendingRef.current = helpId;
    // Step aside when the panel would cover what is being shown
    const target = findHelpTarget(helpId);
    if (target && panelRef.current && overlaps(target.getBoundingClientRect(), panelRef.current.getBoundingClientRect())) {
      setOpen(false);
    }
    channelRef.current?.postMessage({ type: 'spotlight', helpId });
  };

  const openStep = (tourId, step) => window.open(`/help?tour=${tourId}&step=${step}`, 'innocoso-help', HELP_WINDOW);

  if (!open) {
    return (
      <Tooltip title="Ask the assistant" placement="left">
        <button type="button" className="as-bubble" data-help-id="assistant-button" onClick={() => setOpen(true)}>
          <CommentOutlined />
        </button>
      </Tooltip>
    );
  }

  return (
    <div className="as-panel" ref={panelRef}>
      <div className="as-head">
        <div>
          <div className="as-title">Assistant</div>
          <div className="as-sub">How do I …? Ask in any language · 可以用中文提问</div>
        </div>
        <div>
          {items.length > 0 && (
            <Tooltip title="New conversation">
              <Button type="text" size="small" icon={<ClearOutlined />} onClick={() => { setItems([]); setHint(''); }} />
            </Tooltip>
          )}
          <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => setOpen(false)} title="Close" />
        </div>
      </div>

      <div className="as-list" ref={listRef}>
        {items.length === 0 && (
          <div className="as-empty">
            Ask how to do something in the ERP, for example "How do I change the contact person on an invoice?"
            The answers come from the help. Click a <span className="as-ref">highlighted name</span> to see where it is.
            If the help has no answer, you can pass your question on to an admin.
          </div>
        )}
        {items.map((item, i) => {
          if (item.kind === 'q') return <div key={i} className="as-q">{item.text}</div>;
          if (item.kind === 'error') return <div key={i} className="as-error">{item.text}</div>;
          return (
            <AssistantAnswer key={i} id={item.id} text={item.text} covered={item.covered}
              fetchFn={fetchWithAuth} onRef={showElement} onStep={openStep} />
          );
        })}
        {busy && <div className="as-busy"><span /><span /><span /></div>}
      </div>

      {hint && <div className="as-hint">{hint}</div>}

      <div className="as-input">
        <Input.TextArea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onPressEnter={(e) => {
            // Enter also confirms a character in Chinese input methods; that one must not send
            if (e.nativeEvent.isComposing || e.shiftKey) return;
            e.preventDefault();
            send();
          }}
          autoSize={{ minRows: 1, maxRows: 4 }}
          placeholder="Your question"
          maxLength={2000}
          autoFocus
        />
        <Button type="primary" icon={<SendOutlined />} onClick={send} loading={busy} />
      </div>
    </div>
  );
};

export default AssistantChat;
